import { Colors } from "../../config/DesignConstants";
import BaseEntity from "../../core/entity/BaseEntity";
import { V } from "../../core/Vector";
import { VectorButton } from "../graphics/VectorButton";
import { TextAnchor, VectorText, VerticalAnchor } from "../text/VectorText";
import GameFlowManager from "../util/GameFlowManager";
import { HighScoreManager } from "../util/HighScoreManager";

export default class MainMenu extends BaseEntity {
  private title: VectorText;
  private startButton: VectorButton;
  private highScoreText?: VectorText;
  private lastTopScore: number = -1;

  constructor() {
    super();

    // Create title text
    this.title = new VectorText({
      text: "UTOPIA MUST DANCE",
      position: V(0, -100),
      scale: 7.0, // Much larger title
      strokeWidth: 4, // Even thicker lines for retro look
      color: Colors.CYAN,
      anchor: TextAnchor.CENTER,
      layer: "menu",
    });

    // Create start button
    this.startButton = new VectorButton({
      text: "START GAME",
      position: V(0, 50),
      style: "primary",
      size: "large",
      onClick: this.handleStartGame,
      layer: "menu",
    });

    // High score text will be created dynamically in updateHighScores()
  }

  private handleStartGame = () => {
    if (!this.game) return;

    // Create game flow manager (automatically starts game via onAdd)
    this.game.addEntity(new GameFlowManager());

    // Remove the main menu
    this.destroy();
  };

  onAdd(): void {
    // Add child entities
    this.addChild(this.title);
    this.addChild(this.startButton);

    // Update high scores and conditionally add high score UI
    this.refreshHighScores();

    // Center the menu on screen using actual screen dimensions
    this.updatePositions();

    // Dispatch menu start event for music system
    this.dispatch("menuStart", {});
  }

  onTick(): void {
    // Check if high score has changed and refresh if needed
    const highScoreManager = this.game?.entities.getById(
      "highScoreManager",
    ) as HighScoreManager;
    if (highScoreManager) {
      const currentTopScore = highScoreManager.getHighScore();

      if (currentTopScore !== this.lastTopScore) {
        this.refreshHighScores();
        this.updatePositions();
      }
    }
  }

  onResize(): void {
    // Re-center menu items when viewport changes
    this.updatePositions();
  }

  private refreshHighScores(): void {
    // Clear existing high score UI elements
    this.clearHighScoreUI();

    // Rebuild high score UI
    this.updateHighScores();

    // Update the tracked value
    const highScoreManager = this.game?.entities.getById(
      "highScoreManager",
    ) as HighScoreManager;
    this.lastTopScore = highScoreManager?.getHighScore() ?? 0;
  }

  private clearHighScoreUI(): void {
    // Remove high score text if it was added
    if (this.highScoreText) {
      const index = this.children.indexOf(this.highScoreText);
      if (index >= 0) {
        this.children.splice(index, 1);
        this.highScoreText.parent = undefined;
      }
      this.highScoreText = undefined;
    }
  }

  private updateHighScores(): void {
    const highScoreManager = this.game?.entities.getById(
      "highScoreManager",
    ) as HighScoreManager;
    if (!highScoreManager) return;

    const highScore = highScoreManager.getHighScore();

    // Only show high score if there is one
    if (highScore > 0) {
      this.highScoreText = new VectorText({
        text: `HIGH SCORE: ${highScore.toLocaleString()}`,
        scale: 1.5,
        strokeWidth: 1,
        color: Colors.YELLOW,
        anchor: TextAnchor.CENTER,
        verticalAnchor: VerticalAnchor.CENTER,
        layer: "menu",
      });

      this.addChild(this.highScoreText);
    }
  }

  private updatePositions(): void {
    if (!this.game) return;

    // Get actual screen dimensions
    const screenWidth = this.game.renderer.app.renderer.width;
    const screenHeight = this.game.renderer.app.renderer.height;

    const centerX = screenWidth / 2;
    const centerY = screenHeight / 2;

    // Position high score at top of screen
    if (this.highScoreText) {
      this.highScoreText.position = V(centerX, 30);
    }

    // Center the main menu elements
    this.title.position = V(centerX, centerY - 80);
    this.startButton.setPosition(V(centerX, centerY + 20));
  }
}
